import i18n from "@/i18n.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { ApiCustomFieldValue } from "@/types/custom-field.types.ts";
import { ApiDepartment } from "@/types/department.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { ApiOrganization } from "@/types/organization.types.ts";
import { ApiTeam } from "@/types/team.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export enum ApiGoalOwnerType {
  company = "company",
  department = "department",
  team = "team",
  employee = "employee",
}

export enum ApiGoalStatus {
  active = "active",
  completed = "completed",
  failed = "failed",
  cancelled = "cancelled",
}

export enum ApiGoalConfidenceLevel {
  on_track = "on_track",
  at_risk = "at_risk",
  off_track = "off_track",
}

export enum ApiGoalMetricType {
  number = "number",
  percentage = "percentage",
  currency = "currency",
  boolean = "boolean",
}

export const GoalMetricTypeItems = (): SelectItem[] => [
  { id: ApiGoalMetricType.number, title: i18n.t("goals:metric_type_number") },
  { id: ApiGoalMetricType.percentage, title: i18n.t("goals:metric_type_percentage") },
  { id: ApiGoalMetricType.currency, title: i18n.t("goals:metric_type_currency") },
  { id: ApiGoalMetricType.boolean, title: i18n.t("goals:metric_type_boolean") },
];

export const GoalStatusItems = (): SelectItem[] => [
  { id: ApiGoalStatus.active, title: i18n.t("goals:status_active") },
  { id: ApiGoalStatus.completed, title: i18n.t("goals:status_completed") },
  { id: ApiGoalStatus.failed, title: i18n.t("goals:status_failed") },
  { id: ApiGoalStatus.cancelled, title: i18n.t("goals:status_cancelled") },
];

export const GoalConfidenceLevelItems = (): SelectItem[] => [
  { id: ApiGoalConfidenceLevel.on_track, title: i18n.t("goals:on_track") },
  { id: ApiGoalConfidenceLevel.at_risk, title: i18n.t("goals:at_risk") },
  { id: ApiGoalConfidenceLevel.off_track, title: i18n.t("goals:off_track") },
];

export const GoalOwnerTypeItems = (): SelectItem[] => [
  { id: ApiGoalOwnerType.company, title: i18n.t("common:company") },
  { id: ApiGoalOwnerType.department, title: i18n.t("core:department") },
  { id: ApiGoalOwnerType.team, title: i18n.t("core:team") },
  { id: ApiGoalOwnerType.employee, title: i18n.t("core:employee") },
];

export const GoalOwnerTypeInfo = (): SelectItem[] => [
  {
    id: ApiGoalOwnerType.company,
    title: i18n.t("goals:company_goal"),
    caption: i18n.t("goals:company_goal_description"),
  },
  {
    id: ApiGoalOwnerType.department,
    title: i18n.t("goals:department_goal"),
    caption: i18n.t("goals:department_goal_description"),
  },
  {
    id: ApiGoalOwnerType.team,
    title: i18n.t("goals:team_goal"),
    caption: i18n.t("goals:team_goal_description"),
  },
  {
    id: ApiGoalOwnerType.employee,
    title: i18n.t("goals:employee_goal"),
    caption: i18n.t("goals:employee_goal_description"),
  },
];

export interface ApiGoalSearchFilter extends ApiFilterBase {
  parent_goal_id?: number[];
  text?: string;
  owner_type?: ApiGoalOwnerType[];
  owner_id?: number[];
  assignee_ids?: number[];
  is_shared?: boolean;
  date_period_start?: Date;
  date_period_end?: Date;
  status?: ApiGoalStatus[];
  confidence_level?: ApiGoalConfidenceLevel[];
  only_top_level?: boolean;
  only_initiatives?: boolean;
}

export interface ApiGoalsCountDepartment extends ApiDepartment {
  is_archived: boolean;
}

export interface ApiGoalsCountDepartmentItem {
  department: ApiGoalsCountDepartment;
  count: number;
}

export interface ApiGoalsCountTeamItem {
  team: ApiTeam;
  count: number;
}

export interface ApiGoalsCount {
  personal: {
    count: number;
  };
  departments: ApiGoalsCountDepartmentItem[];
  teams: ApiGoalsCountTeamItem[];
  direct_reports: {
    employee_ids: number[];
    count: number;
  };
  subordinates: {
    count: number;
    employee_ids: number[];
  };
}

export interface ApiGoalShort {
  id: number;
  datetime_created_at: Date;
  datetime_updated_at: Date;
  datetime_key_results_update_at: Date;
  parent_goal: ApiGoal | null;
  title: string;
  description: string | null;
  owner_type: ApiGoalOwnerType;
  assign_to: ApiEmployee | null;
  owner_employee: ApiEmployeeShort | null;
  owner_department: ApiDepartment | null;
  owner_company: ApiOrganization | null;
  owner_team: ApiTeam | null;
  is_shared: boolean;
  visible_to: ApiEmployeeShort[];
  date_start: Date;
  date_end: Date;
  status: ApiGoalStatus;
  progress: number | null;
  confidence_level: ApiGoalConfidenceLevel;
  weight: number;
  is_archived: boolean;
  has_children: boolean;
  goal_key_results_count: number;
  parent_goal_id?: number | null;
}

export interface ApiGoal extends ApiGoalShort {
  goal_key_results: ApiGoalKeyResult[];
  acl: ApiGoalAcl;
  parent_goal_chain: ApiGoal;
}

export interface ApiGoalPayload {
  parent_goal_id: number | null;
  title: string;
  description: string | null;
  owner_type: ApiGoalOwnerType;
  owner_id: number | null | undefined;
  assign_to_id: number | null | undefined;
  is_shared: boolean;
  visible_to_ids: number[];
  date_start: Date;
  date_end: Date;
  status: ApiGoalStatus;
  confidence_level: ApiGoalConfidenceLevel;
  weight: number | string;
}

export interface ApiGoalAcl {
  can_view?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  can_cancel?: boolean;
  can_reject?: boolean;
}

export interface ApiGoalKeyResultPayload {
  title: string;
  description: string | null;
  metric_type: ApiGoalMetricType;
  start_value: ApiCustomFieldValue | null;
  target_value: ApiCustomFieldValue | null;
  current_value?: ApiCustomFieldValue | null;
  currency?: string | null;
  confidence_level: ApiGoalConfidenceLevel;
  weight: string;
}
export interface ApiGoalKeyResult {
  id: number;
  datetime_created_at: Date;
  datetime_updated_at: Date;
  goal_id: number;
  title: string;
  description: string | null;
  metric_type: ApiGoalMetricType;
  start_value: ApiCustomFieldValue | null;
  target_value: ApiCustomFieldValue | null;
  current_value: ApiCustomFieldValue | null;
  previous_value?: ApiCustomFieldValue | null;
  currency?: string | null;
  progress: number | null;
  confidence_level: ApiGoalConfidenceLevel;
  weight: number;
}

export interface ApiGoalKeyResultFull extends ApiGoalKeyResult {
  goal_key_result_check_ins: ApiGoalCheckIn[];
}

export interface ApiGoalCheckIn {
  result_session_id: number;
  created_at: Date;
  updated_at: Date;
  goal_id: number;
  key_results: ApiGoalKeyResult[];
  confidence_level: ApiGoalConfidenceLevel;
  current_status: ApiGoalStatus;
  previous_status: ApiGoalStatus;
  current_confidence_level: ApiGoalConfidenceLevel;
  previous_confidence_level: ApiGoalConfidenceLevel;
  comment: ApiComment | null;
  author: ApiEmployeeShort | null;
  datetime_updated_at: Date;
  datetime_adjustment: Date;
}

export interface ApiGoalCheckInPayload {
  confidence_level: ApiGoalConfidenceLevel;
  status: ApiGoalStatus;
  comment: string | null;
  key_results: ApiGoalCheckInKeyResultsPayload[];
  datetime_adjustment: Date | null;
}

export interface ApiGoalCheckInKeyResultsPayload {
  key_result_id: number;
  key_result_check_in: ApiGoalCheckInKeyResultPayload;
}

export interface ApiGoalCheckInKeyResultPayload {
  value: ApiCustomFieldValue | null;
}
